import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
    ChevronLeft,
    ScrollText,
    HeartHandshake,
    Ban,
    Video,
    Download,
    ShieldAlert,
    ExternalLink,
} from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import SEO from "@/components/SEO";
import { api } from "@/lib/api";
import { trackPageview } from "@/lib/analytics";

/**
 * Public /regulamin page - the fan-mod's Terms of Use (regulamin).
 *
 * Written in Polish, professional but approachable tone, covering:
 *   1. The mod is a hobby project and free of charge.
 *   2. Reselling and re-uploads to other servers are forbidden.
 *   3. Streaming and YouTube usage is explicitly allowed.
 *   4. Downloads only from the author's official links.
 *   5. Use at your own risk (no warranty of any kind).
 */
export default function Regulations() {
    const [modInfo, setModInfo] = useState(null);

    useEffect(() => {
        trackPageview("/regulamin");
    }, []);

    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.get("/mod-info");
                setModInfo(data);
            } catch {
                /* keep defaults */
            }
        })();
    }, []);

    // Rules content - keeping the copy inside the component so the layout
    // and the copy live together (easier to iterate on).
    const rules = [
        {
            n: "01",
            icon: HeartHandshake,
            title: "Mod jest hobbystyczny i darmowy",
            body: (
                <>
                    <p>
                        Modyfikacja <b>„Ruda Śląska 1:1”</b> to fanowski
                        projekt do gry <i>Euro Truck Simulator 2</i>,
                        rozwijany całkowicie non-profit. Nie jest w żaden
                        sposób powiązany ze studiem SCS Software ani z
                        oficjalnym wydawcą gry.
                    </p>
                    <p>
                        Pobranie oraz korzystanie z moda jest <b>bezpłatne</b>
                        {" "}i pozostanie takie w każdej wydawanej wersji.
                        Jakiekolwiek dobrowolne wsparcie autora (np. przez
                        BuyCoffee.to) jest wyrazem podziękowania - nie
                        stanowi opłaty za mod ani nie odblokowuje żadnej
                        wersji „premium”.
                    </p>
                </>
            ),
        },
        {
            n: "02",
            icon: Ban,
            title: "Zakaz odsprzedaży i re-uploadu",
            body: (
                <>
                    <p>
                        Modyfikacji <b>nie wolno</b> sprzedawać,
                        wymieniać ani udostępniać w formie płatnej.
                        Zabronione jest również <b>re-uploadowanie</b>
                        {" "}moda oraz jego fragmentów (map, modeli,
                        tekstur, dźwięków, plików <code>.scs</code>) na
                        inne serwery, dyski, chmury czy strony agregujące
                        pliki.
                    </p>
                    <p>
                        Zabronione są również modyfikacje wtórne, czyli
                        publikowanie wersji zmodyfikowanych, „ulepszonych”
                        czy paczek zawierających części tego moda bez
                        wcześniejszej pisemnej zgody autora.
                    </p>
                    <p className="text-[color:var(--text-muted)] text-sm">
                        Jeżeli chcesz podzielić się modem - zawsze linkuj
                        do oficjalnego źródła (patrz punkt 04).
                    </p>
                </>
            ),
        },
        {
            n: "03",
            icon: Video,
            title: "Zezwolenie na streaming i YouTube",
            body: (
                <>
                    <p>
                        Twórcy treści mają <b>pełne pozwolenie</b> na
                        publikowanie materiałów wideo z rozgrywki z
                        udziałem tej modyfikacji - zarówno w formie
                        transmisji na żywo (Twitch, Kick, YouTube Live,
                        itp.), jak i przygotowanych filmów (VOD, shortsy,
                        recenzje, poradniki).
                    </p>
                    <p>
                        Materiały mogą być <b>monetyzowane</b> na kanale
                        twórcy (reklamy YouTube, subskrypcje Twitch,
                        donacje). Prosimy jedynie o krótką wzmiankę o
                        nazwie moda w opisie filmu lub na streamie oraz o
                        link do <Link className="underline hover:text-[color:var(--primary)]" to="/#pobierz">oficjalnej strony pobierania</Link>.
                    </p>
                </>
            ),
        },
        {
            n: "04",
            icon: Download,
            title: "Pobieranie tylko z oficjalnych linków autora",
            body: (
                <>
                    <p>
                        Aby uniknąć fałszywych lub zainfekowanych paczek,
                        zawsze pobieraj mod <b>wyłącznie</b> z
                        oficjalnych źródeł wskazanych na tej stronie
                        (sekcje <Link className="underline hover:text-[color:var(--primary)]" to="/#pobierz">Pobierz</Link>{" "}
                        i <Link className="underline hover:text-[color:var(--primary)]" to="/#archiwum">Archiwum</Link>).
                    </p>
                    <p>
                        Autor nie ponosi odpowiedzialności za zawartość
                        pobraną z <b>nieoficjalnych źródeł</b> - plików
                        udostępnianych na forach, Discordach czy
                        serwisach agregujących. Takie pliki mogą być
                        zmodyfikowane, przestarzałe lub zawierać
                        złośliwe oprogramowanie.
                    </p>
                    <p>
                        Jeśli natrafisz na re-upload lub podejrzaną
                        wersję moda, prosimy o zgłoszenie tego przez
                        sekcję <Link className="underline hover:text-[color:var(--primary)]" to="/#kontakt">Społeczność</Link>{" "}
                        albo formularz <Link className="underline hover:text-[color:var(--primary)]" to="/zglos-blad">Zgłoś błąd</Link>.
                    </p>
                </>
            ),
        },
        {
            n: "05",
            icon: ShieldAlert,
            title: "Użycie na własną odpowiedzialność",
            body: (
                <>
                    <p>
                        Modyfikacja jest udostępniana <b>„taką, jaka
                        jest”</b> (as-is), bez żadnych gwarancji -
                        wyraźnych ani dorozumianych - w tym gwarancji
                        przydatności do konkretnego celu, kompatybilności
                        z konkretną wersją gry ani współpracy z innymi
                        modami.
                    </p>
                    <p>
                        Autor <b>nie ponosi odpowiedzialności</b> za
                        ewentualne szkody powstałe w wyniku instalacji
                        lub używania modyfikacji, w szczególności za
                        uszkodzone zapisy stanu gry (savey), awarie gry,
                        utratę danych czy konflikty z innymi
                        modyfikacjami. Przed instalacją zalecamy wykonanie
                        kopii zapasowej folderu <code>Documents/Euro Truck Simulator 2</code>.
                    </p>
                    <p>
                        Instalując lub uruchamiając modyfikację,
                        potwierdzasz, że zapoznałeś/-aś się z niniejszym
                        regulaminem i akceptujesz jego treść.
                    </p>
                </>
            ),
        },
    ];

    return (
        <div className="App" data-testid="regulations-page">
            <SEO
                modInfo={modInfo}
                title="Regulamin modyfikacji Ruda Śląska 1:1"
                description="Regulamin korzystania z fanowskiej modyfikacji Ruda Śląska 1:1 do Euro Truck Simulator 2 - zasady użytkowania, zakaz re-uploadu, zgody na streaming i YouTube."
                pathname="/regulamin"
            />
            <Header />

            <main
                className="pb-24"
                style={{ paddingTop: "calc(6rem + var(--announcement-h, 0px))" }}
            >
                <section className="max-w-4xl mx-auto px-6">
                    <Link
                        to="/"
                        data-testid="regulations-back-home"
                        className="inline-flex items-center gap-2 text-[color:var(--text-secondary)] hover:text-[color:var(--primary)] text-xs uppercase tracking-[0.25em] mb-6"
                    >
                        <ChevronLeft className="w-4 h-4" />
                        Powrót do strony
                    </Link>

                    <div className="flex items-start gap-4 mb-8">
                        <div className="p-3 rounded-xl bg-[color:var(--primary)]/15 border border-[color:var(--primary)]/40">
                            <ScrollText className="w-7 h-7 text-[color:var(--primary)]" />
                        </div>
                        <div>
                            <div className="text-[10px] uppercase tracking-[0.35em] text-[color:var(--text-secondary)] mb-1">
                                Dokument · Regulamin
                            </div>
                            <h1
                                data-testid="regulations-title"
                                className="text-3xl md:text-5xl font-black text-[color:var(--text-primary)] leading-tight"
                            >
                                Regulamin modyfikacji
                                <span className="block text-[color:var(--primary)]">
                                    Ruda Śląska 1:1
                                </span>
                            </h1>
                            <p className="text-[color:var(--text-secondary)] mt-3 max-w-2xl text-sm md:text-base">
                                Poniższy regulamin określa zasady
                                korzystania z fanowskiej modyfikacji do gry{" "}
                                <i>Euro Truck Simulator 2</i>. Przed
                                pobraniem lub instalacją prosimy o
                                zapoznanie się z jego treścią.
                            </p>
                            <p className="text-[10px] uppercase tracking-[0.25em] text-[color:var(--text-muted)] mt-4">
                                Ostatnia aktualizacja:{" "}
                                {new Date().toLocaleDateString("pl-PL", {
                                    day: "2-digit",
                                    month: "long",
                                    year: "numeric",
                                })}
                            </p>
                        </div>
                    </div>

                    <ol
                        className="space-y-6"
                        data-testid="regulations-list"
                    >
                        {rules.map((r) => {
                            const Icon = r.icon;
                            return (
                                <li
                                    key={r.n}
                                    data-testid={`regulation-item-${r.n}`}
                                    className="panel-yellow p-6 md:p-8"
                                >
                                    <div className="flex items-start gap-4">
                                        <div className="shrink-0 flex flex-col items-center gap-2">
                                            <div className="w-11 h-11 rounded-lg bg-[color:var(--primary)]/15 border border-[color:var(--primary)]/40 flex items-center justify-center">
                                                <Icon className="w-5 h-5 text-[color:var(--primary)]" />
                                            </div>
                                            <span className="text-[10px] uppercase tracking-[0.3em] text-[color:var(--text-muted)] font-bold">
                                                {r.n}
                                            </span>
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <h2 className="text-xl md:text-2xl font-black text-[color:var(--text-primary)] mb-3">
                                                {r.title}
                                            </h2>
                                            <div className="space-y-3 text-sm md:text-base text-[color:var(--text-secondary)] leading-relaxed">
                                                {r.body}
                                            </div>
                                        </div>
                                    </div>
                                </li>
                            );
                        })}
                    </ol>

                    <div className="panel-yellow p-6 md:p-8 mt-8">
                        <div className="label-eyebrow mb-3">Zgłoszenia i kontakt</div>
                        <p className="text-sm text-[color:var(--text-secondary)] leading-relaxed">
                            Wszelkie pytania, wątpliwości oraz zgłoszenia
                            dotyczące niniejszego regulaminu (np. prośby
                            o zgodę na wykorzystanie fragmentów moda w
                            innych projektach) prosimy kierować przez
                            kanały opisane w sekcji{" "}
                            <Link
                                className="underline hover:text-[color:var(--primary)] inline-flex items-center gap-1"
                                to="/#kontakt"
                            >
                                Społeczność <ExternalLink className="w-3 h-3" />
                            </Link>.
                        </p>
                        <p className="text-xs text-[color:var(--text-muted)] mt-4">
                            Autor moda zastrzega sobie prawo do
                            aktualizacji regulaminu. Data ostatniej
                            zmiany widoczna jest u góry strony.
                        </p>
                    </div>
                </section>
            </main>

            <Footer />
        </div>
    );
}
